import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_BASE } from '../../constants/general.constants';
import {
  ExecPlan,
  ExecJob,
  ExecPlanAndJob,
  CreatePlanRequest,
  UpdatePlanRequest,
  CreateJobRequest,
  TriggerRoutineRequest,
  RetryJobRequest,
  JobListParams,
  ExecStatus,
  ExecStep,
  ExecStepFile,
  RoutineValidationResponse,
  RoutineStats,
  RoutineHistoryItem,
  JobStatusSummary,
  JobDetailResponse,
  JobAcknowledgment,
  UpdateAcknowledgmentRequest,
  JobDiffResponse,
  JobReview,
  ReviewRequest,
} from '../../interfaces';
import { isProjectRoutineListGet } from '../../utils/routine-request-url';
import type { WorkflowDeletionImpact } from '../workflow/workflow.service';

@Injectable({
  providedIn: 'root',
})
export class ExecutionPlanService {
  readonly #http = inject(HttpClient);

  /**
   * Helper method to construct execution plan URLs (using /routine endpoint)
   */
  private createPlanUrl(projectId: number, routineId?: number, resource?: string): string {
    let url = `${API_BASE}/project/${projectId}/routine`;
    if (routineId !== undefined) {
      url += `/${routineId}`;
    }
    if (resource) {
      url += `/${resource}`;
    }
    return url;
  }

  /**
   * Some environments return a JSON array; others wrap lists (e.g. `{ items: [...] }`).
   * Without normalization, callers doing `body.map(...)` break or show empty tables.
   */
  private normalizePlanListBody(body: unknown): ExecPlan[] {
    if (Array.isArray(body)) {
      return body;
    }
    if (body && typeof body === 'object') {
      const o = body as Record<string, unknown>;
      for (const key of ['items', 'routines', 'plans', 'data', 'results']) {
        const v = o[key];
        if (Array.isArray(v)) {
          return v as ExecPlan[];
        }
      }
    }
    return [];
  }

  private normalizeJobListBody(body: unknown): ExecJob[] {
    if (Array.isArray(body)) {
      return body;
    }
    if (body && typeof body === 'object') {
      const o = body as Record<string, unknown>;
      for (const key of ['items', 'jobs', 'data', 'results']) {
        const v = o[key];
        if (Array.isArray(v)) {
          return v as ExecJob[];
        }
      }
    }
    return [];
  }

  private extractTotalCountFromBody(body: unknown): number | undefined {
    if (body && typeof body === 'object' && !Array.isArray(body)) {
      const o = body as Record<string, unknown>;
      const t = o['total'] ?? o['totalCount'] ?? o['count'];
      if (typeof t === 'number' && Number.isFinite(t)) {
        return t;
      }
    }
    return undefined;
  }

  /**
   * Helper method to handle HTTP parameters for job listing
   */
  private buildJobParams(params?: JobListParams): HttpParams {
    if (!params) {
      return new HttpParams();
    }

    let httpParams = new HttpParams();

    // Handle array or single value parameters
    httpParams = this.addArrayOrSingleParam(httpParams, 'status', params.status);
    httpParams = this.addArrayOrSingleParam(httpParams, 'source', params.source);

    // Handle simple string/number parameters
    httpParams = this.addSimpleParam(httpParams, 'sort', params.sort);
    httpParams = this.addSimpleParam(httpParams, 'offset', params.offset);
    httpParams = this.addSimpleParam(httpParams, 'limit', params.limit);
    httpParams = this.addSimpleParam(httpParams, 'desc', params.desc);
    httpParams = this.addSimpleParam(httpParams, 'runsAsId', params.runsAsId);
    httpParams = this.addSimpleParam(httpParams, 'startedSince', params.startedSince);

    return httpParams;
  }

  /**
   * Helper method to add parameters that can be arrays or single values
   */
  private addArrayOrSingleParam(
    httpParams: HttpParams,
    paramName: string,
    value: string | string[] | ExecStatus | ExecStatus[] | undefined
  ): HttpParams {
    if (value === undefined || value === null) {
      return httpParams;
    }

    if (Array.isArray(value)) {
      value.forEach(item => {
        httpParams = httpParams.append(paramName, String(item));
      });
    } else {
      httpParams = httpParams.set(paramName, String(value));
    }

    return httpParams;
  }

  /**
   * Helper method to add simple parameters with automatic string conversion
   */
  private addSimpleParam(
    httpParams: HttpParams,
    paramName: string,
    value: string | number | boolean | undefined
  ): HttpParams {
    if (value !== undefined) {
      httpParams = httpParams.set(paramName, value.toString());
    }
    return httpParams;
  }


  // ──────────────────────────────────────────────────────────────────────────────
  // Plan Management Methods
  // ──────────────────────────────────────────────────────────────────────────────

  /**
   * Lists all execution plans for a project
   * @param projectId The ID of the project
   * @returns Observable containing array of execution plans
   */
  getPlans(projectId: number): Observable<ExecPlan[]> {
    const url = this.createPlanUrl(projectId);
    return this.#http.get<unknown>(url).pipe(
      map((body) => this.normalizePlanListBody(body)),
      catchError((err: unknown) => {
        const status = err instanceof HttpErrorResponse ? err.status : undefined;
        // SCRUM-380: nc-server may respond with 500 when a project has no routines; treat as empty list.
        // Only 500 — not 502/503 — so gateway/proxy outages still surface to users and the interceptor.
        if (
          status === 500 &&
          isProjectRoutineListGet(url, 'GET')
        ) {
          console.warn(
            `[ExecutionPlanService] getPlans(${projectId}): HTTP 500 on routine list; using empty list`
          );
          return of([]);
        }
        return throwError(() => err);
      }),
    );
  }

  /**
   * Creates a new execution plan
   * @param projectId The ID of the project
   * @param request The plan creation request
   * @returns Observable containing the created plan and potentially an initial job
   */
  createPlan(projectId: number, request: CreatePlanRequest): Observable<ExecPlanAndJob> {
    return this.#http.post<ExecPlanAndJob>(this.createPlanUrl(projectId), request);
  }

  /**
   * Gets a specific execution plan by ID
   * @param projectId The ID of the project
   * @param routineId The ID of the routine
   * @returns Observable containing the plan details
   */
  getPlan(projectId: number, routineId: number): Observable<ExecPlan> {
    return this.#http.get<ExecPlan>(this.createPlanUrl(projectId, routineId));
  }

  /**
   * Resolve a routine by display name (SCRUM-259 / nc-server).
   */
  getPlanByRoutineName(projectId: number, name: string): Observable<ExecPlan> {
    return this.#http.get<ExecPlan>(
      `${API_BASE}/project/${projectId}/routine/by-name/${encodeURIComponent(name)}`
    );
  }

  /**
   * Deletes a routine and its jobs. A routine whose jobs have started is
   * deleted only with `deleteHistory`; without it the server answers 409 with
   * `details.job_count` (EHQ-536). Use RoutineDeleteService, which asks first.
   * @param projectId The ID of the project
   * @param routineId The ID of the routine to delete
   * @param deleteHistory Confirms that the routine's job history goes too
   * @returns Observable for the deletion operation
   */
  deletePlan(projectId: number, routineId: number, deleteHistory = false): Observable<void> {
    const options = deleteHistory ? { params: { delete_history: 'true' } } : {};
    return this.#http.delete<void>(this.createPlanUrl(projectId, routineId), options);
  }

  /**
   * What deleting a routine would remove: its jobs that have started, and how
   * many are still executing (EHQ-536).
   */
  getDeletionImpact(projectId: number, routineId: number): Observable<WorkflowDeletionImpact> {
    return this.#http.get<WorkflowDeletionImpact>(
      this.createPlanUrl(projectId, routineId, 'deletion-impact'),
    );
  }

  /**
   * Updates an existing execution plan
   * @param projectId The ID of the project
   * @param routineId The ID of the routine to update
   * @param request The plan update request
   * @returns Observable containing the updated plan and potentially a new job
   */
  updatePlan(projectId: number, routineId: number, request: UpdatePlanRequest): Observable<ExecPlanAndJob> {
    return this.#http.patch<ExecPlanAndJob>(this.createPlanUrl(projectId, routineId), request);
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Job Management Methods
  // ──────────────────────────────────────────────────────────────────────────────

  /**
   * Lists jobs for a specific execution plan with filtering and pagination
   * @param projectId The ID of the project
   * @param routineId The ID of the routine
   * @param params Optional filtering and pagination parameters
   * @returns Observable containing array of jobs
   */
  getJobs(projectId: number, routineId: number, params?: JobListParams): Observable<ExecJob[]> {
    const httpParams = this.buildJobParams(params);

    return this.#http
      .get<unknown>(this.createPlanUrl(projectId, routineId, 'job'), { params: httpParams })
      .pipe(map((body) => this.normalizeJobListBody(body)));
  }

  /**
   * Lists jobs for a specific execution plan with filtering, sorting and pagination,
   * returning the total count when the server provides it via X-Total-Count header
   */
  searchJobs(
    projectId: number,
    routineId: number,
    params?: JobListParams
  ): Observable<{ items: ExecJob[]; total?: number }> {
    const httpParams = this.buildJobParams(params);
    return this.#http
      .get<unknown>(this.createPlanUrl(projectId, routineId, 'job'), {
        params: httpParams,
        observe: 'response',
      })
      .pipe(
        map((resp: HttpResponse<unknown>) => {
          const totalHeader = resp.headers.get('X-Total-Count');
          let total: number | undefined;
          if (totalHeader) {
            const parsed = parseInt(totalHeader, 10);
            if (!Number.isNaN(parsed)) {
              total = parsed;
            }
          }
          if (total === undefined) {
            total = this.extractTotalCountFromBody(resp.body);
          }
          const items = this.normalizeJobListBody(resp.body);
          return { items, total };
        }),
      );
  }

  /**
   * Manually creates a new job for a routine
   * @param projectId The ID of the project
   * @param routineId The ID of the routine
   * @param request Optional job creation parameters (snake_case - server expects it)
   * @returns Observable containing the created job
   */
  createJob(projectId: number, routineId: number, request?: CreateJobRequest): Observable<ExecJob> {
    const body = request || {};

    return this.#http.post<ExecJob>(
      this.createPlanUrl(projectId, routineId, 'job'),
      body
    );
  }

  /**
   * Gets details of a specific job
   * @param projectId The ID of the project
   * @param routineId The ID of the routine
   * @param jobId The ID of the job
   * @returns Observable containing the job details and status
   */
  getJob(projectId: number, routineId: number, jobId: number): Observable<ExecJob> {
    return this.#http.get<ExecJob>(
      this.createPlanUrl(projectId, routineId, `job/${jobId}`)
    );
  }

  /**
   * Gets lightweight job status summary (optimized for polling)
   * @param projectId The ID of the project
   * @param routineId The ID of the routine
   * @param jobId The ID of the job
   * @returns Observable containing the job status summary
   */
  getJobStatus(projectId: number, routineId: number, jobId: number): Observable<JobStatusSummary> {
    return this.#http.get<JobStatusSummary>(
      this.createPlanUrl(projectId, routineId, `job/${jobId}/status`)
    );
  }

  /**
   * Deletes a job (may create new scheduled job based on crontab)
   * @param projectId The ID of the project
   * @param routineId The ID of the routine
   * @param jobId The ID of the job to delete
   * @returns Observable for the deletion operation
   */
  deleteJob(projectId: number, routineId: number, jobId: number): Observable<void> {
    return this.#http.delete<void>(
      this.createPlanUrl(projectId, routineId, `job/${jobId}`)
    );
  }

  /**
   * Requests cancellation of a running, pending, or scheduled job
   */
  cancelJob(projectId: number, routineId: number, jobId: number): Observable<ExecJob> {
    return this.#http.post<ExecJob>(
      this.createPlanUrl(projectId, routineId, `job/${jobId}/cancel`),
      {}
    );
  }

  /**
   * Retries a failed/canceled job.
   * Server expects camelCase (JobRetryParams has `rename_all = "camelCase"`).
   */
  retryJob(
    projectId: number,
    routineId: number,
    jobId: number,
    body: RetryJobRequest = {}
  ): Observable<ExecJob> {
    return this.#http.post<ExecJob>(
      this.createPlanUrl(projectId, routineId, `job/${jobId}/retry`),
      body
    );
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Job Detail & Acknowledgment Methods
  // ──────────────────────────────────────────────────────────────────────────────

  getJobDetail(projectId: number, routineId: number, jobId: number): Observable<JobDetailResponse> {
    return this.#http.get<JobDetailResponse>(
      this.createPlanUrl(projectId, routineId, `job/${jobId}/detail`)
    );
  }

  getJobAcknowledgment(projectId: number, routineId: number, jobId: number): Observable<JobAcknowledgment | null> {
    return this.#http.get<JobAcknowledgment | null>(
      this.createPlanUrl(projectId, routineId, `job/${jobId}/acknowledgment`)
    );
  }

  updateJobAcknowledgment(
    projectId: number,
    routineId: number,
    jobId: number,
    request: UpdateAcknowledgmentRequest
  ): Observable<JobAcknowledgment> {
    return this.#http.put<JobAcknowledgment>(
      this.createPlanUrl(projectId, routineId, `job/${jobId}/acknowledgment`),
      request
    );
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Job Review Methods (SCRUM-292)
  // ──────────────────────────────────────────────────────────────────────────────

  submitJobReview(
    projectId: number,
    routineId: number,
    jobId: number,
    request: ReviewRequest
  ): Observable<JobReview> {
    return this.#http.post<JobReview>(
      this.createPlanUrl(projectId, routineId, `job/${jobId}/review`),
      request
    );
  }

  getJobReview(projectId: number, routineId: number, jobId: number): Observable<JobReview | null> {
    return this.#http.get<JobReview | null>(
      this.createPlanUrl(projectId, routineId, `job/${jobId}/review`)
    );
  }

  /**
   * Compares two jobs from the same routine, returning step-level diffs
   */
  getJobDiff(
    projectId: number,
    routineId: number,
    leftJobId: number,
    rightJobId: number
  ): Observable<JobDiffResponse> {
    const params = new HttpParams()
      .set('left', leftJobId.toString())
      .set('right', rightJobId.toString());
    return this.#http.get<JobDiffResponse>(
      this.createPlanUrl(projectId, routineId, 'job/diff'),
      { params }
    );
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Job Steps Management Methods
  // ──────────────────────────────────────────────────────────────────────────────

  /**
   * Gets all steps for a specific job
   */
  getJobSteps(projectId: number, routineId: number, jobId: number): Observable<ExecStep[]> {
    return this.#http.get<ExecStep[]>(
      this.createPlanUrl(projectId, routineId, `job/${jobId}/steps`)
    );
  }

  /**
   * Gets plain text logs for a specific job step
   */
  getStepLogs(projectId: number, routineId: number, jobId: number, stepId: number): Observable<string> {
    return this.#http.get(
      this.createPlanUrl(projectId, routineId, `job/${jobId}/steps/${stepId}/logs`),
      { responseType: 'text' }
    );
  }

  /**
   * Gets details of a specific job step
   */
  getJobStep(projectId: number, routineId: number, jobId: number, stepId: number): Observable<ExecStep> {
    return this.#http.get<ExecStep>(
      this.createPlanUrl(projectId, routineId, `job/${jobId}/steps/${stepId}`)
    );
  }

  /**
   * Gets every file change recorded across the whole job, ordered by step.
   * This is the raw change log — a name can appear several times (Added then
   * Modified) and may end Deleted; callers wanting the final filesystem state
   * must reduce it (see JobDetailPageComponent's review file list).
   */
  getJobFiles(projectId: number, routineId: number, jobId: number): Observable<ExecStepFile[]> {
    return this.#http.get<ExecStepFile[]>(
      this.createPlanUrl(projectId, routineId, `job/${jobId}/files`)
    );
  }

  /**
   * Gets the content of a job file by file id, without needing its step.
   */
  getJobFileContent(projectId: number, routineId: number, jobId: number, fileId: number): Observable<Blob> {
    return this.#http.get(
      this.createPlanUrl(projectId, routineId, `job/${jobId}/file/${fileId}`),
      { responseType: 'blob' }
    );
  }

  /**
   * Gets files changed in a specific step
   */
  getStepFiles(projectId: number, routineId: number, jobId: number, stepId: number): Observable<ExecStepFile[]> {
    return this.#http.get<ExecStepFile[]>(
      this.createPlanUrl(projectId, routineId, `job/${jobId}/steps/${stepId}/files`)
    );
  }

  /**
   * Gets the content of a file changed in a step
   */
  getStepFileContent(projectId: number, routineId: number, jobId: number, stepId: number, fileId: number): Observable<Blob> {
    return this.#http.get(
      this.createPlanUrl(projectId, routineId, `job/${jobId}/steps/${stepId}/files/${fileId}/content`),
      { responseType: 'blob' }
    );
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Routine Enhanced Features
  // ──────────────────────────────────────────────────────────────────────────────

  /**
   * Validates a routine configuration without creating it
   */
  validatePlan(projectId: number, request: CreatePlanRequest | UpdatePlanRequest): Observable<RoutineValidationResponse> {
    return this.#http.post<RoutineValidationResponse>(
      this.createPlanUrl(projectId, undefined, 'validate'),
      request
    );
  }

  /**
   * Pause or resume a routine
   */
  setPlanPaused(projectId: number, routineId: number, paused: boolean): Observable<ExecPlanAndJob> {
    return this.#http.patch<ExecPlanAndJob>(
      this.createPlanUrl(projectId, routineId, 'pause'),
      { paused }
    );
  }

  /**
   * Trigger routine immediately, optionally with overrides.
   * Server expects snake_case (TriggerParams has NO rename_all).
   */
  triggerPlan(
    projectId: number,
    routineId: number,
    body: TriggerRoutineRequest = {}
  ): Observable<ExecJob> {
    return this.#http.post<ExecJob>(
      this.createPlanUrl(projectId, routineId, 'trigger'),
      body
    );
  }

  /**
   * Fetch a workflow by ID (used to get variable_schema for the run dialog).
   */
  getWorkflow(projectId: number, versionId: number, workflowId: number): Observable<any> {
    return this.#http.get<any>(
      `${API_BASE}/project/${projectId}/v/${versionId}/workflow/${workflowId}`
    );
  }

  /**
   * Fetch workflows for a version and find one by name.
   * Used when a routine has workflowName but no workflowId (name-based resolution).
   */
  getWorkflowByName(projectId: number, versionId: number, workflowName: string): Observable<any> {
    return this.#http.get<any[]>(
      `${API_BASE}/project/${projectId}/v/${versionId}/workflow`
    ).pipe(
      map(workflows => workflows.find(w => w.name === workflowName) || null)
    );
  }

  /**
   * Get routine statistics with optional time range
   */
  getPlanStats(
    projectId: number,
    routineId: number,
    opts?: { since?: string; until?: string }
  ): Observable<RoutineStats> {
    let params = new HttpParams();
    if (opts?.since) params = params.set('since', opts.since);
    if (opts?.until) params = params.set('until', opts.until);
    return this.#http.get<RoutineStats>(this.createPlanUrl(projectId, routineId, 'stats'), { params });
  }

  /**
   * Get recent job history for a routine
   */
  getPlanHistory(projectId: number, routineId: number, limit?: number): Observable<RoutineHistoryItem[]> {
    let params = new HttpParams();
    if (limit !== undefined) params = params.set('limit', limit.toString());
    return this.#http.get<RoutineHistoryItem[]>(this.createPlanUrl(projectId, routineId, 'history'), { params });
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Utility Methods
  // ──────────────────────────────────────────────────────────────────────────────

  /**
   * Validates a cron expression format (enhanced validation)
   * @param crontab The cron expression to validate
   * @returns Object with validation result and error message
   */
  validateCronExpression(crontab: string): { isValid: boolean; error?: string } {
    if (!crontab || !crontab.trim()) {
      return { isValid: true }; // Empty is valid (manual only)
    }

    // Basic cron validation - should have 5 parts (minute hour day month dayOfWeek)
    const parts = crontab.trim().split(/\s+/);

    if (parts.length !== 5) {
      return {
        isValid: false,
        error: `Invalid cron format. Expected 5 fields, got ${parts.length}. Format: minute hour day month weekday`
      };
    }

    const [minute, hour, day, month, weekday] = parts;

    // Validate minute (0-59)
    if (!this.isValidCronField(minute, 0, 59)) {
      return { isValid: false, error: 'Invalid minute value. Must be 0-59 or * or */n' };
    }

    // Validate hour (0-23)
    if (!this.isValidCronField(hour, 0, 23)) {
      return { isValid: false, error: 'Invalid hour value. Must be 0-23 or * or */n' };
    }

    // Validate day (1-31)
    if (!this.isValidCronField(day, 1, 31)) {
      return { isValid: false, error: 'Invalid day value. Must be 1-31 or * or */n' };
    }

    // Validate month (1-12)
    if (!this.isValidCronField(month, 1, 12)) {
      return { isValid: false, error: 'Invalid month value. Must be 1-12 or * or */n' };
    }

    // Validate weekday (0-7, where 0 and 7 are Sunday)
    if (!this.isValidCronField(weekday, 0, 7)) {
      return { isValid: false, error: 'Invalid weekday value. Must be 0-7 or * or */n (0=Sunday, 7=Sunday)' };
    }

    return { isValid: true };
  }

  /**
   * Helper method to validate individual cron fields
   */
  private isValidCronField(field: string, min: number, max: number): boolean {
    // Allow wildcard
    if (field === '*') return true;

    // Allow step values like */5
    if (field.startsWith('*/')) {
      const step = parseInt(field.substring(2));
      return !isNaN(step) && step > 0;
    }

    // Allow ranges like 1-5
    if (field.includes('-')) {
      const [start, end] = field.split('-').map(Number);
      return !isNaN(start) && !isNaN(end) && start >= min && end <= max && start <= end;
    }

    // Allow lists like 1,3,5
    if (field.includes(',')) {
      return field.split(',').every(val => {
        const num = parseInt(val);
        return !isNaN(num) && num >= min && num <= max;
      });
    }

    // Allow single numbers
    const num = parseInt(field);
    return !isNaN(num) && num >= min && num <= max;
  }

  /**
   * Helper method to get human-readable status description
   */
  getStatusDescription(status: ExecStatus): string {
    switch (status) {
      case ExecStatus.Pending:
        return 'Waiting to be scheduled';
      case ExecStatus.Scheduled:
        return 'Scheduled for execution';
      case ExecStatus.Executing:
        return 'Currently running';
      case ExecStatus.Completed:
        return 'Completed successfully';
      case ExecStatus.Failed:
        return 'Execution failed';
      case ExecStatus.Canceled:
        return 'Execution was canceled';
      case ExecStatus.Retry:
        return 'Retry requested — will try again';
      case ExecStatus.Aborted:
        return 'Aborted by workflow logic';
      default:
        return 'Unknown status';
    }
  }

  /**
   * Helper method to get common cron expressions
   */
  getCommonCronExpressions(): { [key: string]: string } {
    return {
      'Every minute': '* * * * *',
      'Every hour': '0 * * * *',
      'Daily at 9 AM': '0 9 * * *',
      'Weekly on Monday at 9 AM': '0 9 * * 1',
      'Monthly on 1st at 9 AM': '0 9 1 * *',
      'Weekdays at 9 AM': '0 9 * * 1-5',
      'Every 15 minutes': '*/15 * * * *',
      'Twice daily (9 AM & 5 PM)': '0 9,17 * * *'
    };
  }
}
